import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import {
  selectHasInlineOracle,
  selectIsChatAiOpen,
  selectIsOracleAvailable,
  selectOracleHideBottomMenu,
  setChatAiIsOpenActionCreator,
} from '~/redux/slices/chat-ai'
import { selectIsActivitySurface } from '~/aioracle/slices/learning-context.slice'
import { Breakpoint, useScreenBreakPoints } from '~/utils/useBreakpoint'

const ChatAiContent = dynamic(() => import('./ChatAiContent'))

// Mounted once from _app, above the page tree, so a route change no longer unmounts the open oracle.
export default function ChatAiPanel(): React.ReactElement | null {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsChatAiOpen)
  const isAvailable = useAppSelector(selectIsOracleAvailable)
  const hideBottomMenu = useAppSelector(selectOracleHideBottomMenu)
  const besideActivity = useAppSelector(selectIsActivitySurface)
  const hasInlineOracle = useAppSelector(selectHasInlineOracle)

  const [shouldShow, setShouldShow] = useState(isOpen)
  const [modalScaleZero, setModalScaleZero] = useState(!isOpen)

  // We are sending this as a prop to ChatAiContent to fix a bug where the modals initial render on desktop was the mobile version.
  // That scrolled the page to the bottom and disabled the scroll.
  const isMobile = useScreenBreakPoints({ lt: Breakpoint.MD })

  useEffect(() => {
    if (besideActivity) {
      setShouldShow(false)
      setModalScaleZero(true)
      return
    }

    if (isOpen) {
      setShouldShow(true)
      const scaleUp = setTimeout(() => setModalScaleZero(false), 300)

      return () => clearTimeout(scaleUp)
    }

    setModalScaleZero(true)
    const unmount = setTimeout(() => setShouldShow(false), 300)

    return () => clearTimeout(unmount)
  }, [isOpen, besideActivity])

  // Leaving an oracle-less route closes it. Deferred a tick so a 0 between revoke and declare cancels.
  useEffect(() => {
    if (isAvailable || !isOpen) return

    const close = setTimeout(() => {
      dispatch(setChatAiIsOpenActionCreator(false))
    }, 0)

    return () => clearTimeout(close)
  }, [dispatch, isAvailable, isOpen])

  if (besideActivity || hasInlineOracle || !(isAvailable && shouldShow)) return null

  return <ChatAiContent hideBottomMenu={hideBottomMenu} isMobile={isMobile} modalScaleZero={modalScaleZero} />
}
